import type * as React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';

import type { Button, ButtonProps, InlineNotificationProps } from '@coursera/cds-core';

/**
 * Interface defining the structure of a notification
 * @interface NotificationState
 */
export interface NotificationState {
  /** Whether the notification is currently visible */
  isVisible: boolean;
  /** Notification ID */
  notificationId: string;
  /** Title text of the notification */
  title: string;
  /** Main content of the notification, can be a string or React element */
  content: React.ReactElement | string;
  /** Severity level of the notification */
  severity: InlineNotificationProps['severity'];
  /** Optional inline action button */
  inlineAction?: React.ReactElement<ButtonProps, typeof Button>;
  /** Optional callback when notification is dismissed */
  onDismiss?: () => void;
  /** Whether to show the close button (default: true) */
  isDismissible?: boolean;
  /** Whether to auto-close the notification */
  autoClose?: boolean;
  /** Delay in milliseconds before auto-closing (default: 5000) */
  autoCloseDelay?: number;
}

const defaultNotificationState: NotificationState = {
  isVisible: false,
  notificationId: '',
  title: '',
  content: '',
  severity: 'information',
  autoClose: false,
  autoCloseDelay: 3000,
};

/**
 * Hook for managing notification state.
 * @internal
 * This hook is intended for internal use only by TeamsContainer to provide notification functionality through TeamsContext.
 * Components should access notification functionality through TeamsContext instead of using this hook directly.
 *
 * @returns {Object} Notification state and control functions
 * @returns {NotificationState} notification - Current notification state
 * @returns {Function} showNotification - Function to show a new notification
 * @returns {Function} hideNotification - Function to hide the current notification
 */
export const useNotificationState = () => {
  const [notification, setNotification] = useState<NotificationState>(defaultNotificationState);

  const timeoutRef = useRef<NodeJS.Timeout>();

  const hideNotification = useCallback<(notificationId?: string) => void>((notificationId) => {
    setNotification((prev) => {
      if (typeof notificationId !== 'undefined' && prev.notificationId !== notificationId) {
        return prev;
      }

      return {
        ...prev,
        isVisible: false,
      };
    });
  }, []);

  const showNotification = useCallback<(notification: Omit<NotificationState, 'isVisible'>) => void>(
    (newNotification) => {
      // Clear any existing timeout
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      setNotification({
        ...newNotification,
        isVisible: true,
        autoClose: newNotification.autoClose ?? false,
        autoCloseDelay: newNotification.autoCloseDelay ?? 5000,
      });

      // Scroll to top to ensure notification is visible
      if (typeof window !== 'undefined') {
        window.scrollTo({
          top: 0,
          behavior: 'smooth',
        });
      }
    },
    []
  );

  // Clear notification on initial mount

  // Handle auto-close and cleanup
  useEffect(() => {
    if (notification.isVisible && notification.autoClose) {
      timeoutRef.current = setTimeout(() => {
        hideNotification();
      }, notification.autoCloseDelay);
    }

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [notification, hideNotification]);

  return {
    notification,
    showNotification,
    hideNotification,
    isNotificationVisible: useCallback<(id: string) => boolean>(
      (id) => notification.isVisible && notification.notificationId === id,
      [notification.isVisible, notification.notificationId]
    ),
  };
};
